import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Badge,
  Filters,
  TextField,
  Spinner,
  Pagination,
  Avatar,
  InlineStack
} from '@shopify/polaris'
import { format } from 'date-fns'

import { useAuth } from '@/contexts/AuthContext'
import { customerApi } from '@/services/api'
import type { CustomerFilters } from '@/types'

export default function CustomersPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [filters, setFilters] = useState<CustomerFilters>({
    page: 1,
    limit: 20
  })
  const [searchValue, setSearchValue] = useState('')

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['customers', user?.storeId, filters],
    queryFn: () => customerApi.getCustomers(user!.storeId, filters),
    enabled: !!user?.storeId
  })

  const customers = customersData?.data.data.customers || []
  const pagination = customersData?.data.data.pagination

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    setFilters(prev => ({ ...prev, search: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const renderCustomerItem = (customer: any) => {
    const lastMessageDate = customer.lastMessageAt
      ? format(new Date(customer.lastMessageAt), 'MMM d, yyyy')
      : 'Never'

    return (
      <ResourceItem
        id={customer.id}
        onClick={() => navigate(`/customers/${customer.id}`)}
        accessibilityLabel={`Customer ${customer.name || customer.whatsappPhoneNumber}`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar
            customer
            name={customer.name || customer.whatsappPhoneNumber}
            size="medium"
          />

          <div style={{ flex: 1 }}>
            <InlineStack align="space-between" blockAlign="center">
              <div>
                <Text variant="bodyMd" fontWeight="semibold" as="h3">
                  {customer.name || 'Unknown Customer'}
                </Text>
                <Text variant="bodySm" color="subdued" as="p">
                  {customer.whatsappPhoneNumber}
                </Text>
                {customer.email && (
                  <Text variant="bodySm" color="subdued" as="p">
                    {customer.email}
                  </Text>
                )}
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ marginBottom: '4px' }}>
                  <Badge status="info">
                    {customer.conversationCount} conversations
                  </Badge>
                </div>
                <Text variant="bodySm" color="subdued" as="p">
                  Last message: {lastMessageDate}
                </Text>
                {customer._count?.orders > 0 && (
                  <div style={{ marginTop: '4px' }}>
                    <Badge status="success">
                      {customer._count.orders} orders
                    </Badge>
                  </div>
                )}
              </div>
            </InlineStack>

            {customer.tags && customer.tags.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {customer.tags.slice(0, 3).map((tag: string, index: number) => (
                  <Badge key={index} status="attention">
                    {tag}
                  </Badge>
                ))}
                {customer.tags.length > 3 && (
                  <Badge>+{customer.tags.length - 3} more</Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </ResourceItem>
    )
  }

  const appliedFilters = []
  if (filters.search) {
    appliedFilters.push({
      key: 'search',
      label: `Search: ${filters.search}`,
      onRemove: () => {
        setSearchValue('')
        setFilters(prev => ({ ...prev, search: undefined }))
      }
    })
  }

  const filterControl = (
    <Filters
      queryValue={searchValue}
      filters={[]}
      appliedFilters={appliedFilters}
      onQueryChange={handleSearchChange}
      onQueryClear={() => handleSearchChange('')}
      onClearAll={() => {
        setSearchValue('')
        setFilters({ page: 1, limit: 20 })
      }}
    >
      <div style={{ paddingLeft: '8px' }}>
        <TextField
          label=""
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Search customers by name, phone, or email..."
          clearButton
          onClearButtonClick={() => handleSearchChange('')}
        />
      </div>
    </Filters>
  )

  if (isLoading) {
    return (
      <Page title="Customers">
        <Layout>
          <Layout.Section>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spinner accessibilityLabel="Loading customers" size="large" />
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title="Customers"
      subtitle={`${pagination?.total || 0} customers`}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <ResourceList
              resourceName={{ singular: 'customer', plural: 'customers' }}
              items={customers}
              renderItem={renderCustomerItem}
              filterControl={filterControl}
              emptyState={
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Text variant="headingMd" as="h3">
                    No customers found
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    {searchValue
                      ? 'Try adjusting your search terms'
                      : 'Customers will appear here when they start conversations on WhatsApp'
                    }
                  </Text>
                </div>
              }
            />

            {pagination && pagination.pages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px'
              }}>
                <Pagination
                  hasPrevious={pagination.page > 1}
                  onPrevious={() => handlePageChange(pagination.page - 1)}
                  hasNext={pagination.page < pagination.pages}
                  onNext={() => handlePageChange(pagination.page + 1)}
                  label={`Page ${pagination.page} of ${pagination.pages}`}
                />
              </div>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}